import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, InfoIcon } from 'lucide-react';
import { AnimatedScreen } from '../ui/AnimatedScreen';
import { VRPConfig } from './VRPSetupScreen';
import { Account } from './AccountSelectionScreen';
export interface VRPAuthorizationScreenProps {
  tppName: string;
  merchantName: string;
  merchantLogo?: string;
  vrpConfig: VRPConfig;
  currency: string;
  reference: string;
  paymentPurpose: string;
  accounts: Account[];
  selectedAccountId?: string;
  onAuthorize: (accountId: string) => void;
  onCancel: () => void;
}
export function VRPAuthorizationScreen({
  tppName,
  merchantName,
  merchantLogo,
  vrpConfig,
  currency,
  reference,
  paymentPurpose,
  accounts,
  selectedAccountId,
  onAuthorize,
  onCancel
}: VRPAuthorizationScreenProps) {
  const [selectedAccount, setSelectedAccount] = useState<string>(selectedAccountId || accounts[0]?.id || '');
  const handleAuthorize = () => {
    if (selectedAccount) {
      onAuthorize(selectedAccount);
    }
  };
  // Format frequency for display
  const frequencyDisplay = {
    'one-time': 'ONE-TIME',
    daily: 'DAILY',
    weekly: 'WEEK',
    monthly: 'MONTH',
    quarterly: 'QUARTER',
    annual: 'YEAR'
  }[vrpConfig.frequency];
  // Calculate first payment date (today)
  const firstPaymentDate = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  // Calculate stop payment date
  const calculateStopDate = () => {
    const date = new Date();
    const multiplier = vrpConfig.totalPaymentsAllowed || 12;
    switch (vrpConfig.frequency) {
      case 'daily':
        date.setDate(date.getDate() + multiplier);
        break;
      case 'weekly':
        date.setDate(date.getDate() + multiplier * 7);
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + multiplier);
        break;
      case 'quarterly':
        date.setMonth(date.getMonth() + multiplier * 3);
        break;
      case 'annual':
        date.setFullYear(date.getFullYear() + multiplier);
        break;
    }
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  const stopPaymentDate = calculateStopDate();
  const totalValueAllowed = vrpConfig.maxAmountPerPayment * (vrpConfig.totalPaymentsAllowed || 12);
  return <AnimatedScreen data-id="element-1236">
      <div className="space-y-6" data-id="element-1237">
        {/* Header */}
        <div data-id="element-1238">
          <h1 className="text-4xl font-bold mb-3 text-gray-900 leading-tight" data-id="element-1239">
            Authorize Payment Permission
          </h1>
          <p className="text-base text-gray-600" data-id="element-1240">
            Review the payment rules and select which account to use for{' '}
            <span className="font-semibold text-gray-900" data-id="element-1241">{merchantName}</span>{' '}
            payments.
          </p>
        </div>

        {/* Merchant Card - Clean & Simple */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="p-5 rounded-2xl bg-white border-2 border-gray-200" data-id="element-1242">
          <div className="flex items-center gap-4 mb-4" data-id="element-1243">
            {merchantLogo && <div className="w-16 h-16 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center p-2" data-id="element-1244">
                <img src={merchantLogo} alt={merchantName} className="w-full h-full object-contain" data-id="element-1245" />
              </div>}
            <div data-id="element-1246">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-1247">
                {merchantName}
              </h2>
              <p className="text-sm text-gray-500" data-id="element-1248">Recurring bill payment</p>
            </div>
          </div>

          <div className="space-y-2 pt-3 border-t border-gray-200" data-id="element-1249">
            <div className="flex justify-between" data-id="element-1250">
              <span className="text-base text-gray-600" data-id="element-1251">Reference</span>
              <span className="text-base font-semibold text-gray-900" data-id="element-1252">
                {reference}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-1253">
              <span className="text-base text-gray-600" data-id="element-1254">Payment Purpose</span>
              <span className="text-base font-semibold text-gray-900" data-id="element-1255">
                {paymentPurpose === 'utility-bill' ? 'Utility Bill' : paymentPurpose}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Payment Rules - Clean Table */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} data-id="element-1256">
          <div className="flex items-center gap-2 mb-4" data-id="element-1257">
            <InfoIcon className="w-6 h-6 text-gray-900" data-id="element-1258" />
            <h3 className="text-xl font-bold text-gray-900 uppercase" data-id="element-1259">
              Payment Rules
            </h3>
          </div>

          <div className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden" data-id="element-1260">
            <div className="divide-y divide-gray-200" data-id="element-1261">
              <div className="flex justify-between p-4" data-id="element-1262">
                <span className="text-base text-gray-600" data-id="element-1263">Max per Payment</span>
                <span className="text-base font-bold text-gray-900" data-id="element-1264">
                  {currency} {vrpConfig.maxAmountPerPayment.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between p-4" data-id="element-1265">
                <span className="text-base text-gray-600" data-id="element-1266">
                  First Payment Date
                </span>
                <span className="text-base font-semibold text-gray-900" data-id="element-1267">
                  {firstPaymentDate}
                </span>
              </div>
              <div className="flex justify-between p-4" data-id="element-1268">
                <span className="text-base text-gray-600" data-id="element-1269">Payment Repeats</span>
                <span className="text-base font-bold text-gray-900" data-id="element-1270">
                  {frequencyDisplay}
                </span>
              </div>
              <div className="flex justify-between p-4" data-id="element-1271">
                <span className="text-base text-gray-600" data-id="element-1272">Stop payment on</span>
                <span className="text-base font-semibold text-gray-900" data-id="element-1273">
                  {stopPaymentDate}
                </span>
              </div>
              <div className="flex justify-between p-4" data-id="element-1274">
                <span className="text-base text-gray-600" data-id="element-1275">
                  Total Payments Allowed
                </span>
                <span className="text-base font-bold text-gray-900" data-id="element-1276">
                  {vrpConfig.totalPaymentsAllowed || 12}
                </span>
              </div>
              <div className="flex justify-between p-5 bg-gray-50" data-id="element-1277">
                <span className="text-lg font-bold text-gray-900" data-id="element-1278">
                  Total Value Allowed
                </span>
                <span className="text-2xl font-bold text-gray-900" data-id="element-1279">
                  {currency} {totalValueAllowed.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Account Selection */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} data-id="element-1280">
          <h3 className="text-xl font-bold text-gray-900 uppercase mb-4" data-id="element-1281">
            Select Payment Account
          </h3>

          <div className="space-y-3" data-id="element-1282">
            {accounts.map(account => <button key={account.id} onClick={() => setSelectedAccount(account.id)} className={`w-full p-5 rounded-2xl border-2 transition-all text-left ${selectedAccount === account.id ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white'}`} data-id="element-1283">
                <div className="flex items-center justify-between" data-id="element-1284">
                  <div data-id="element-1285">
                    <div className="text-lg font-bold text-gray-900 mb-1" data-id="element-1286">
                      {account.name}
                    </div>
                    <div className="text-sm font-mono text-gray-500" data-id="element-1287">
                      {account.accountNumber.replace(/(\d{4})(?=\d)/g, '$1 ')}
                    </div>
                  </div>
                  {selectedAccount === account.id && <CheckCircle2Icon className="w-6 h-6 text-teal-600" data-id="element-1288" />}
                </div>
              </button>)}
          </div>
        </motion.div>

        {/* Balance Check Notice */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="p-4 rounded-2xl bg-blue-50 border-2 border-blue-200" data-id="element-1289">
          <p className="text-sm text-blue-900" data-id="element-1290">
            <span className="font-semibold" data-id="element-1291">{tppName}</span> will be able to
            check your balance before making each payment to ensure sufficient
            funds.
          </p>
        </motion.div>

        {/* Action Buttons */}
        <div className="space-y-3 pt-2" data-id="element-1292">
          {/* Cancel Button */}
          <button onClick={onCancel} className="w-full px-6 py-4 bg-white border-2 border-gray-300 rounded-full font-semibold text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1293">
            Cancel
          </button>

          {/* Authorize Button */}
          <button onClick={handleAuthorize} disabled={!selectedAccount} className="w-full px-6 py-4 text-white font-bold text-lg transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{
          background: 'linear-gradient(90deg, #00BCD4 0%, #1976D2 50%, #000000 100%)',
          borderRadius: '50px'
        }} data-id="element-1294">
            <div className="flex items-center justify-center gap-3" data-id="element-1295">
              <img src="https://cdn.magicpatterns.com/uploads/8Zyr2eTymP8hQsszbaoPhJ/White_Mark.svg" alt="ALTAREQ" className="h-6 w-6 object-contain" data-id="element-1296" />
              <span data-id="element-1297">Authorize using Al Tareq</span>
            </div>
          </button>
        </div>
      </div>
    </AnimatedScreen>;
}